import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FileText, Users, MessageSquare, Calendar, TrendingUp, BarChart } from 'lucide-react'

interface PageData {
  path: string
  fullUrl?: string
  title: string
  pageType?: string
  chatCount: number
  uniqueVisitors: number
  bookingClicks?: number
  bookingCompletions?: number
  avgMessages?: string
  clickConversionRate?: number
  bookingConversionRate?: number
}

interface TopPagesProps {
  data: PageData[]
  loading?: boolean
}

export default function TopPages({ data, loading = false }: TopPagesProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Pages</CardTitle>
          <CardDescription>Pages where visitors engage with chat</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Pages</CardTitle>
          <CardDescription>Pages where visitors engage with chat</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground text-sm">
            No page data available yet. Start chatting to see which pages drive engagement!
          </div>
        </CardContent>
      </Card>
    )
  }

  const maxChats = Math.max(...data.map(p => p.chatCount), 1)
  const hasConversionData = data.some(p => p.bookingClicks !== undefined)

  // Calculate totals
  const totals = data.reduce((acc, p) => ({
    chats: acc.chats + p.chatCount,
    visitors: acc.visitors + p.uniqueVisitors,
    bookingClicks: acc.bookingClicks + (p.bookingClicks || 0),
    bookingCompletions: acc.bookingCompletions + (p.bookingCompletions || 0)
  }), { chats: 0, visitors: 0, bookingClicks: 0, bookingCompletions: 0 })

  const overallConversionRate = totals.chats > 0
    ? ((totals.bookingCompletions / totals.chats) * 100).toFixed(1)
    : '0.0'

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle>Top Pages</CardTitle>
          <CardDescription>Pages where visitors engage with chat and book appointments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.map((page, index) => {
              const barWidth = (page.chatCount / maxChats) * 100

              return (
                <div key={page.path} className="space-y-2">
                  {/* Page Info */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="font-mono text-sm font-semibold truncate cursor-help">
                              {page.path}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">{page.fullUrl || page.path}</p>
                          </TooltipContent>
                        </Tooltip>
                        {index === 0 && (
                          <Badge variant="default" className="text-xs">Top</Badge>
                        )}
                        {page.pageType && page.pageType !== 'unknown' && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {page.pageType}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{page.title}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* Unique Visitors */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span className="text-sm font-medium">{page.uniqueVisitors}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Unique Visitors</TooltipContent>
                      </Tooltip>

                      {/* Chat Count */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1">
                            <MessageSquare className="h-4 w-4 text-blue-600" />
                            <span className="text-lg font-bold">{page.chatCount}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Chats Started</TooltipContent>
                      </Tooltip>

                      {/* Booking Clicks */}
                      {hasConversionData && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 text-amber-600">
                              <Calendar className="h-4 w-4" />
                              <span className="text-sm font-medium">{page.bookingClicks || 0}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>Booking Clicks</TooltipContent>
                        </Tooltip>
                      )}

                      {/* Booking Completions */}
                      {hasConversionData && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 text-green-600">
                              <TrendingUp className="h-4 w-4" />
                              <span className="text-sm font-bold">{page.bookingCompletions || 0}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-center">
                              <div>Bookings Completed</div>
                              <div className="text-green-400 font-semibold">
                                {page.bookingConversionRate?.toFixed(1) || '0.0'}% conversion
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-colors duration-500"
                      style={{ width: `${barWidth}%` }}
                    />
                    {hasConversionData && page.bookingClicks && page.bookingClicks > 0 && (
                      <div
                        className="absolute top-0 h-full bg-amber-500 opacity-60"
                        style={{
                          left: `${barWidth}%`,
                          width: `${Math.min((page.bookingClicks / maxChats) * 100, 100 - barWidth)}%`
                        }}
                      />
                    )}
                  </div>

                  {/* Avg Messages indicator */}
                  {page.avgMessages && parseFloat(page.avgMessages) > 0 && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <BarChart className="h-3 w-3" />
                      <span>{page.avgMessages} avg messages/chat</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Summary */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className={`grid gap-3 md:gap-4 text-center ${hasConversionData ? 'grid-cols-3 md:grid-cols-5' : 'grid-cols-3'}`}>
              <div>
                <div className="text-xl md:text-2xl font-bold text-blue-600">{data.length}</div>
                <div className="text-xs text-muted-foreground">Pages</div>
              </div>
              <div>
                <div className="text-xl md:text-2xl font-bold">{totals.chats}</div>
                <div className="text-xs text-muted-foreground">Chats</div>
              </div>
              <div>
                <div className="text-xl md:text-2xl font-bold">{totals.visitors}</div>
                <div className="text-xs text-muted-foreground">Visitors</div>
              </div>
              {hasConversionData && (
                <>
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-green-600">{totals.bookingCompletions}</div>
                    <div className="text-xs text-muted-foreground">Bookings</div>
                  </div>
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-amber-600">{overallConversionRate}%</div>
                    <div className="text-xs text-muted-foreground">Conv. Rate</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
